import camelize from 'camelize';
import {SummaryUserStats} from 'records/RepProfileSummary';
import RepProfileCallActivity, {DonutStat, TeamCallActivity} from 'records/RepProfileCallActivity';
import searchResultToCallDetails from 'utils/searchResultToCallDetails';
import RepProfileCoachingActivity, {TeamCoachingActivity} from 'records/RepProfileCoachingActivity';
import {userSchema} from 'records/User';
import urlWithParams from 'utils/urlWithParams';
import cancelIfSending from './cancelIfSending';
import {Field} from 'records/Search';
import {List, Map} from 'immutable';
import {callsApi, dashboardApi} from 'sources/api';

const dateFormat = 'YYYY-MM-DD';
export const userKeys = Object.keys(userSchema).map(camelize);

const callMetrics = ['conversations', 'totalduration', 'avgduration', 'talklisten', 'interchanges'];

const coachingGivenMetrics = [
  'listening_count',
  'listening_pct',
  'comments_count',
  'library_adds',
  'calls_scored',
];

// coaching received has a few more metrics than coaching given
const coachingReceivedMetrics = [
  ...coachingGivenMetrics,
  'listening_time',
  'comments_length',
  'highlights_count',
];

// is the direction overall a "review" / "coaching given" direction or a "received" direction?
const isCoachingGiven = direction => direction === 'coach' || direction === 'review';

// for the metrics in question, hit the user level metrics api and pluck out the user in question
// TODO this is not a long term solution
// dear reader, please rewrite these rollups so they can load incrementally.
const collapseMetricsFor = async ({
  metrics,
  teamId,
  userId,
  startDate,
  endDate,
  metricLevel = 'users',
  direction = undefined,
  stat = undefined,
  cancelConfig,
}) => {
  const teamMetrics = await Promise.all(
    metrics.map(metric =>
      dashboardApi
        .getJson(
          urlWithParams(
            `/metrics/${metricLevel}/${metric}`,
            {
              teams: [teamId],
              start_date: startDate.format(dateFormat),
              end_date: endDate.format(dateFormat),
              direction,
              stat,
            },
            cancelConfig
          )
        )
        .then(rslt => [metric, rslt])
    )
  );

  return teamMetrics
    .map(([metric, result]) => {
      // if team level, we receive the value in "stat", otherwise fish it out of the rslts
      if (metricLevel === 'team') {
        return [metric, result.stat];
      }

      const metricResultForUser = result.find(m => m.id === userId);

      return [metric, metricResultForUser ? metricResultForUser.value : null];
    })
    .reduce((acc, cur) => ({...acc, [cur[0]]: cur[1]}), {});
};

export default cancelIfSending({
  getSummary(userId, startDate, endDate, cancelConfig) {
    return dashboardApi
      .getJson(
        urlWithParams(`/summary/${userId}`, {
          start_date: startDate.format(dateFormat),
          end_date: endDate.format(dateFormat),
        }),
        cancelConfig
      )
      .then(data => {
        return new SummaryUserStats(data);
      });
  },

  getTeamScorecardAverage(teamId, startDate, endDate, cancelConfig) {
    return dashboardApi
      .getJson(
        urlWithParams(`/metrics/team/scoring`, {
          teams: [teamId],
          start_date: startDate.format(dateFormat),
          end_date: endDate.format(dateFormat),
          direction: 'any',
          stat: 'AVG',
        }),
        cancelConfig
      )
      .then(data => data.stat);
  },

  getCallActivity(teamId, userId, startDate, endDate, cancelConfig) {
    return collapseMetricsFor({
      metrics: callMetrics,
      teamId,
      userId,
      startDate,
      endDate,
      cancelConfig,
    }).then(data => {
      return RepProfileCallActivity({
        callsPlaced: data.conversations,
        totalCallDurationSeconds: data.totalduration,
        avgCallDurationSeconds: data.avgduration,
        talkListenRatio: data.talklisten,
        avgInterchangesPerMinute: data.interchanges,
      });
    });
  },

  getCallDonut(teamId, startDate, endDate, cancelConfig) {
    return dashboardApi
      .getJson(
        urlWithParams(`/metrics/users/call_activity`, {
          start_date: startDate.format(dateFormat),
          end_date: endDate.format(dateFormat),
          teams: [teamId],
        }),
        cancelConfig
      )
      .then(data => {
        return new List(
          data.map(
            d =>
              new DonutStat({
                userId: d.id,
                score: d.value,
              })
          )
        );
      });
  },

  getCallTeamMetrics(teamId, userId, startDate, endDate, cancelConfig) {
    const averages = collapseMetricsFor({
      metrics: callMetrics,
      teamId,
      startDate,
      endDate,
      stat: 'AVG',
      metricLevel: 'team',
      cancelConfig,
    });

    const maxes = collapseMetricsFor({
      metrics: callMetrics,
      teamId,
      startDate,
      endDate,
      stat: 'MAX',
      metricLevel: 'team',
      cancelConfig,
    });

    return Promise.all([averages, maxes]).then(([averages, maxes]) => {
      return new TeamCallActivity({
        id: teamId,
        callsPlacedMax: maxes.conversations,
        callsPlacedAvg: averages.conversations,
        totalCallDurationSecondsMax: maxes.totalduration,
        totalCallDurationSecondsAvg: averages.totalduration,
        avgCallDurationSecondsMax: maxes.avgduration,
        avgCallDurationSecondsAvg: averages.avgduration,
        talkListenRatioMax: maxes.talklisten,
        talkListenRatioAvg: averages.talklisten,
        avgInterchangesPerMinuteMax: maxes.interchanges,
        avgInterchangesPerMinuteAvg: averages.interchanges,
      });
    });
  },

  getTeamConversationTrend(teamId, startDate, endDate, cancelConfig) {
    return dashboardApi
      .getJson(
        urlWithParams(
          `/trend/team/conversations/`,
          {
            start_date: startDate.format(dateFormat),
            end_date: endDate.format(dateFormat),
            teams: [teamId],
          },
          cancelConfig
        )
      )
      .then(r => Map(r).map(d => List(d.data)));
  },

  getTeamCallDurationTrend(teamId, startDate, endDate, cancelConfig) {
    return dashboardApi
      .getJson(
        urlWithParams(
          `/trend/team/totalduration/`,
          {
            start_date: startDate.format(dateFormat),
            end_date: endDate.format(dateFormat),
            teams: [teamId],
          },
          cancelConfig
        )
      )
      .then(r => Map(r).map(d => List(d.data)));
  },

  getCoachingActivity(direction, teamId, userId, startDate, endDate, cancelConfig) {
    return collapseMetricsFor({
      metrics: isCoachingGiven(direction) ? coachingGivenMetrics : coachingReceivedMetrics,
      teamId,
      userId,
      startDate,
      endDate,
      direction: direction,
      cancelConfig,
    }).then(data => {
      return RepProfileCoachingActivity({
        callsListenedTo: data.listening_count,
        avgEachCallListenedTo: data.listening_pct,
        commentsCreated: data.comments_count,
        callsAddedToLibraries: data.library_adds,
        scoreCardsCompleted: data.calls_scored,
        totalTimeListened: data.listening_time,
        commentLength: data.comments_length,
        highlightsCreated: data.highlights_count,
      });
    });
  },

  getCoachingTeamMetrics(direction, teamId, userId, startDate, endDate, cancelConfig) {
    const averages = collapseMetricsFor({
      metrics: isCoachingGiven(direction) ? coachingGivenMetrics : coachingReceivedMetrics,
      teamId,
      startDate,
      endDate,
      stat: 'AVG',
      metricLevel: 'team',
      direction,
      cancelConfig,
    });

    const maxes = collapseMetricsFor({
      metrics: isCoachingGiven(direction) ? coachingGivenMetrics : coachingReceivedMetrics,
      teamId,
      startDate,
      endDate,
      stat: 'MAX',
      metricLevel: 'team',
      direction,
      cancelConfig,
    });

    return Promise.all([averages, maxes]).then(([averages, maxes]) => {
      return new TeamCoachingActivity({
        id: teamId,
        callsListenedToMax: maxes.listening_count,
        callsListenedToAvg: averages.listening_count,
        avgEachCallListenedToMax: maxes.listening_pct,
        avgEachCallListenedToAvg: averages.listening_pct,
        commentsCreatedMax: maxes.comments_count,
        commentsCreatedAvg: averages.comments_count,
        callsAddedToLibrariesMax: maxes.library_adds,
        callsAddedToLibrariesAvg: averages.library_adds,
        scoreCardsCompletedMax: maxes.calls_scored,
        scoreCardsCompletedAvg: averages.calls_scored,
        totalTimeListenedMax: maxes.listening_time,
        totalTimeListenedAvg: averages.listening_time,
        commentLengthMax: maxes.comments_length,
        commentLengthAvg: averages.comments_length,
        highlightsCreatedMax: maxes.highlights_count,
        highlightsCreatedAvg: averages.highlights_count,
      });
    });
  },

  getCallMetrics({startDate, endDate, teamIds}) {
    const interchanges = dashboardApi.getJson(`/metrics/users/interchanges`, {
      params: {
        startDate: startDate.format(dateFormat),
        endDate: endDate.format(dateFormat),
        teams: teamIds,
      },
    });

    const talkListen = dashboardApi.getJson(`/metrics/users/talklisten`, {
      params: {
        startDate: startDate.format(dateFormat),
        endDate: endDate.format(dateFormat),
        teams: teamIds,
      },
    });

    return Promise.all([interchanges, talkListen]);
  },

  getCallDetails(userId, date, cancelConfig) {
    return callsApi
      .post(
        'search',
        {
          fields: {
            Caller: {
              target: 'rep',
              type: 'some_of',
              values: [userId],
            },
            Date: {
              gte: date.toISOString(),
              lte: date.clone().add(1, 'day').toISOString(),
            },
          },
        },
        cancelConfig
      )
      .then(resp => [
        List(resp.data.results.map(searchResultToCallDetails)),
        List(resp.data.fields.map(f => new Field(f))),
      ]);
  },

  getTrendData({userId, type, startDate, endDate, direction, context}) {
    return dashboardApi.getJson(`/trend/rep/${userId}/${type}`, {
      params: {
        startDate: startDate.format(dateFormat),
        endDate: endDate.format(dateFormat),
        ...(direction && {direction}),
        ...(context && {context}),
      },
    });
  },
});
